import CheckCircleOutlinedIcon from '@mui/icons-material/CheckCircleOutlined';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { StatusTag } from '@ovComponents/2-component/statusTag/statusTag';
import { formatMoneyValue } from '@utils/moneyFormatting';
import { Box } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import type { OptimizerAuditRollup } from '../../types/audit.types';
import type { BulkRebalanceRun } from '../../types/run.types';

export type BulkRebalanceRunOutcomeStripProps = {
  run: BulkRebalanceRun;
  auditRollup: OptimizerAuditRollup | null;
};

export const BulkRebalanceRunOutcomeStrip = ({
  run,
  auditRollup,
}: BulkRebalanceRunOutcomeStripProps) => {
  const { t } = useTranslation(['bulkRebalance']);
  const summary = run.portfolioSummary;

  return (
    <Box
      data-testid='bulk-rebalance-run-outcome-strip'
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}
    >
      {summary && summary.succeeded > 0 && (
        <StatusTag
          icon={CheckCircleOutlinedIcon}
          label={t('outcome.rebalanced', { count: summary.succeeded })}
          color='positive'
          size='small'
        />
      )}

      {summary && summary.failed > 0 && (
        <StatusTag
          icon={HighlightOffIcon}
          label={t('outcome.failed', { count: summary.failed })}
          color='negative'
          size='small'
        />
      )}

      {summary && summary.skipped > 0 && (
        <StatusTag
          icon={RemoveCircleOutlineIcon}
          label={t('outcome.skipped', { count: summary.skipped })}
          color='warning'
          size='small'
        />
      )}

      {auditRollup && auditRollup.totalTradeCount > 0 && (
        <>
          <StatusTag
            icon={SwapHorizIcon}
            label={t('audit.proposedTrades', {
              count: auditRollup.totalTradeCount,
            })}
            color='informative'
            size='small'
          />
          {auditRollup.totalBuyCents > 0 && (
            <StatusTag
              icon={TrendingUpIcon}
              label={t('audit.totalBuys', {
                value: formatMoneyValue(auditRollup.totalBuyCents),
              })}
              color='positive'
              size='small'
            />
          )}
          {auditRollup.totalSellCents > 0 && (
            <StatusTag
              icon={TrendingDownIcon}
              label={t('audit.totalSells', {
                value: formatMoneyValue(auditRollup.totalSellCents),
              })}
              color='negative'
              size='small'
            />
          )}
        </>
      )}
    </Box>
  );
};
